import type { SmsCatalog, SmsKey, SmsWeatherPhrases } from './sms.ts';

/**
 * §9.2 SMS copy, English: verbatim from the spec table. A message with an "or call {number}"
 * clause has two texts: `withNumber` (verbatim) and `withoutNumber`, the same text with only
 * that clause removed, for orgs whose country pack has no voice number (§9.2 "No voice number").
 * The welcome's reply clause is `smsReplyHintEn`, which `renderSms` appends only for packs with
 * two-way SMS; catalogue text + hint is the §9.2 cell. Punctuation is GSM-7 only ("-", never "—"
 * or "·"; spec §19 Q15), so every English text is GSM-7.
 */
export const smsEn: SmsCatalog<SmsKey> = {
  welcome: {
    withNumber:
      "Hi {first}, I'm TalkOlog, the daily report assistant for {company} at {site}. Each workday before {deadline}, tap your link and talk for ~3 min, or call {number}: {link}",
    withoutNumber:
      "Hi {first}, I'm TalkOlog, the daily report assistant for {company} at {site}. Each workday before {deadline}, tap your link and talk for ~3 min: {link}",
  },
  verify_code: 'TalkOlog code: {code}. It expires in 10 minutes.',
  daily_link: {
    withNumber: "Today's report for {site}: {link} - or just call {number}.",
    withoutNumber: "Today's report for {site}: {link}",
  },
  call_coming: "I'll call you at {time} for today's report. Prefer to do it now? {link}",
  weather_headsup:
    'Heads-up: report due at {deadline}. {weather_phrase} - mention the hours lost and what stopped.',
  reminder_1: 'No report yet for {site} today. A one-minute voice note is plenty: {link}',
  reminder_2: {
    withNumber:
      "Still nothing for today. I'll let the office know at {time}. {link} or call {number}.",
    withoutNumber: "Still nothing for today. I'll let the office know at {time}. {link}",
  },
  missed_call: {
    withNumber: "Missed you for today's report. Call {number} or tap {link}.",
    withoutNumber: "Missed you for today's report. Tap {link}.",
  },
  question_ready: "One quick question about today's report: {link}",
  draft_ready: 'Your report is ready to check: {link}',
  filed: 'Filed: Daily Report {report_no}. PDF: {pdf_link}',
  photos_link: "Add photos to today's report: {link}",
  edit_applied: 'Updated {weekday}: {change}. Version {v} sent to the office.',
  finish_report: 'Your call ended before filing. Finish in one tap: {link}',
  site_paused: '{site} is paused in TalkOlog. Ask the office to resume it.',
  unknown_number: "This number isn't set up with TalkOlog. Ask your office to add you.",
  help: {
    withNumber:
      'TalkOlog daily reports for {company}. Your link: {link} - Call {number} - Text CALL or TEXT to switch - STOP to opt out. Msg & data rates may apply.',
    withoutNumber:
      'TalkOlog daily reports for {company}. Your link: {link} - Text CALL or TEXT to switch - STOP to opt out. Msg & data rates may apply.',
  },
  stop_confirm: "You won't get more texts from TalkOlog. Reply START to resume.",
  pref_call_ok: 'OK, your daily report is now set to a call. Reply TEXT to switch to a link.',
  pref_text_ok:
    'OK, your daily report is now set to a link by text. Reply CALL to switch to a call.',
  // W2-12 (spec §19 Q83, Q102): the report keywords' replies, two-way packs only.
  day_off_link: 'Add the details for {site} today: {link}',
  edit_preview: '{weekday}: {change}? Confirm here: {link}',
  edit_unclear: "I couldn't make that change by text. Make it in Past reports: {link}",
  // W2-16 (spec §19 Q85, Q40): WhatsApp only.
  whatsapp_use_link: 'Voice notes go through your link: {link}',
  whatsapp_stop_confirm:
    "You won't get more WhatsApp messages from TalkOlog. Reply START to resume.",
};

/** The `welcome` reply clause for packs with two-way SMS (§9.2), appended by `renderSms`. */
export const smsReplyHintEn = ' - Reply STOP to opt out, HELP for help';

/**
 * The heads-up's `{weather_phrase}` (§9.2 `weather_headsup`; W2-06), from the site-day's weather
 * snapshot (`renderWeatherPhrase`). GSM-7 only.
 */
export const smsWeatherPhraseEn: SmsWeatherPhrases = {
  rainMorning: 'It rained this morning ({amount})',
  rainAfternoon: 'It rained this afternoon ({amount})',
  rainDay: 'It rained today ({amount})',
  gusts: 'Gusts up to {speed}',
  gustsAfterRain: 'gusts up to {speed}',
  and: ' and ',
  fallback: 'Weather hit the site today',
  decimal: '.',
};

/**
 * W4-06 (spec §9.1): HELP's privacy clause, appended by `renderSms` when the pack names a privacy
 * notice (`SMS_HELP_PRIVACY`). GSM-7.
 */
export const smsHelpPrivacyEn = ' - Privacy: {privacy_link}';

/**
 * W4-05 (spec §19 Q232): Settings' "Send me a test message", texted to the number an admin types
 * (never a reporter). GSM-7, one segment.
 */
export const smsTestMessageEn =
  'TalkOlog test message for {company}. Your site managers get their texts from this sender. No reply needed.';
